/**
 * SEARCH-002 — Palette de recherche globale ⌘K, SHELL MOBILE (top-sheet).
 *
 * Sur mobile, la fusion du champ (desktop) ne s'applique pas et il n'y a PLUS de
 * champ persistant : une poignée-chevron dans le header (`AppHeader` +
 * `SearchRevealHandle`) ouvre ce TOP-SHEET ancré en haut, coins bas arrondis,
 * plafonné 85dvh (page assombrie visible dessous). On peut fermer en tirant vers
 * le HAUT (`usePullGesture`, depuis n'importe où quand la liste est en haut — ne
 * gêne pas le scroll des résultats), au tap sur la page, à la croix ou Échap.
 * Champ collé en haut sous la safe-area + clavier auto (`autoFocus`, anti-zoom). L'aperçu
 * document est un `Sheet` bas (swipe-to-dismiss + safe-areas du DS). Le moteur et
 * le corps sont PARTAGÉS avec le desktop via `useSearchPalette` / `SearchPaletteBody`.
 *
 * `active = isMobile` → le shell desktop (`SearchGlobal`) et celui-ci ne requêtent
 * jamais en même temps (un seul est « actif » selon le breakpoint).
 */

import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { Command } from '@/components/ui/command'
import { useIsMobile } from '@/hooks/use-mobile'
import { useGlobalSearch } from '@/hooks/useGlobalSearch'
import { usePullGesture } from '@/hooks/useSwipeToDismiss'
import { PaletteInput, SearchPaletteBody } from '@/components/search/SearchPalette'
import { useSearchPalette } from '@/components/search/useSearchPalette'
import { SearchPreviewPanel } from '@/components/search/SearchPreviewPanel'
import { SearchDocumentQuickLook } from '@/components/search/SearchDocumentQuickLook'
import { SearchEmailComposer } from '@/components/search/SearchEmailComposer'

interface GlobalSearchDialogProps {
    enableAssistant: boolean
    userId: string
}

export function GlobalSearchDialog({ enableAssistant, userId }: GlobalSearchDialogProps) {
    const { t } = useTranslation()
    const { isOpen, close } = useGlobalSearch()
    const isMobile = useIsMobile() ?? false
    const palette = useSearchPalette({ enableAssistant, userId, active: isMobile })

    const handleClose = () => {
        palette.rememberQuery()
        close()
    }
    // Fermeture par geste : tirer vers le HAUT depuis n'importe où sur le panneau
    // quand la liste est en haut (usePullGesture ne vole pas le scroll des résultats).
    usePullGesture({ enabled: isOpen && isMobile, direction: 'up', onTrigger: handleClose })

    return (
        <>
            <Dialog
                open={isOpen && isMobile}
                onOpenChange={(o) => {
                    if (!o) handleClose()
                }}
            >
                <DialogContent
                    hideCloseButton
                    /* Top-sheet : ancré en haut, pleine largeur, coins bas arrondis,
                       plafonné 85dvh (page assombrie visible dessous). Glisse du haut.

                       `data-panel="top-sheet"` DÉCLARE cette forme. Le détecteur de
                       modales (`tests/mobile/modales.spec.ts`) exige une gouttière
                       latérale de toute modale — parce qu'une modale bord à bord cesse
                       de se lire comme une couche, et que c'est exactement ce qui a
                       rendu « Réglages de la couverture » impossible à fermer. Ici la
                       pleine largeur est le motif, pas un oubli : on le dit, plutôt que
                       d'inventer une règle géométrique qui masquerait le vrai défaut. */
                    data-panel="top-sheet"
                    className="left-0 right-0 top-0 sm:top-0 w-full max-w-none translate-x-0 translate-y-0 sm:translate-y-0 max-h-[85dvh] sm:max-h-[85dvh] gap-0 border-0 rounded-t-none rounded-b-2xl p-0 overflow-hidden shadow-lg duration-200 data-[state=open]:slide-in-from-top data-[state=closed]:slide-out-to-top data-[state=open]:motion-reduce:animate-none"
                >
                    <DialogTitle className="sr-only">{t('globalSearch.title')}</DialogTitle>
                    <Command
                        shouldFilter={false}
                        value={palette.selectedValue}
                        onValueChange={palette.setSelectedValue}
                        onKeyDown={palette.handleCommandKeyDown}
                        className="flex h-full flex-col"
                    >
                        {/* Barre du champ : collée en haut (safe-area) + croix 44px.
                            Porte le geste swipe-up-to-close (drag vers le haut). */}
                        <div className="flex items-center gap-2 border-b border-border px-4 pt-[calc(0.5rem+env(safe-area-inset-top))]">
                            <PaletteInput
                                palette={palette}
                                inputRef={palette.inputRef}
                                autoFocus
                                inputClassName="h-14 min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground"
                            />
                            <button
                                type="button"
                                onClick={handleClose}
                                aria-label={t('common.close', 'Fermer')}
                                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent"
                            >
                                <X className="h-5 w-5" aria-hidden="true" />
                            </button>
                        </div>

                        <SearchPaletteBody palette={palette} variant="mobile" />
                    </Command>

                    {/* Aperçu document — Sheet bas (swipe-to-dismiss + safe-areas du DS). */}
                    <Sheet
                        open={!!palette.mobilePreview}
                        onOpenChange={(o) => {
                            if (!o) palette.setMobilePreview(null)
                        }}
                    >
                        <SheetContent side="bottom" className="max-h-[75dvh] overflow-y-auto p-0">
                            <SheetTitle className="sr-only">{t('globalSearch.preview.title')}</SheetTitle>
                            <SearchPreviewPanel
                                data={palette.mobilePreviewQuery.data}
                                isLoading={palette.mobilePreviewQuery.isLoading}
                                isError={palette.mobilePreviewQuery.isError}
                                onRetry={() => void palette.mobilePreviewQuery.refetch()}
                                currentEntityId={palette.entityId}
                                onQuickLook={palette.openQuickLook}
                                onSendEmail={palette.openComposeEmail}
                                onNavigate={palette.go}
                                onOpen={() => {
                                    const target = palette.mobilePreview
                                    if (!target) return
                                    const hit = palette.hits.find((h) => h.type === target.type && h.id === target.id)
                                    if (hit) palette.openHit(hit)
                                }}
                            />
                        </SheetContent>
                    </Sheet>
                </DialogContent>
            </Dialog>

            {palette.quickLook && (
                <SearchDocumentQuickLook
                    open
                    onOpenChange={(o) => {
                        if (!o) palette.setQuickLook(null)
                    }}
                    fileId={palette.quickLook.fileId}
                    filename={palette.quickLook.filename}
                    mime={palette.quickLook.mime}
                    entityId={palette.quickLook.entityId ?? palette.entityId ?? ''}
                />
            )}

            <SearchEmailComposer
                open={!!palette.composeEmail}
                recipientEmail={palette.composeEmail?.email ?? ''}
                entityId={palette.entityId ?? ''}
                onOpenChange={(o) => {
                    if (!o) palette.setComposeEmail(null)
                }}
            />
        </>
    )
}
